import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CollageSetService } from '../../services/collage-set.service';
import { AppStateService } from '../../services/app-state.service';
import { AuthService } from '../../services/auth.service';
import { UploadDialogService } from '../../components/upload-dialog/upload-dialog.component';
import { PricingDetailDialogService } from '../../components/pricing-detail-dialog/pricing-detail-dialog.component';
import { DataLayerService } from '../../services/datalayer.service';

const DEFAULT_IMAGE = 'https://storage.googleapis.com/static-mahattaart-com/400x400/media/EVRM_HISL044_EC870_H.JPG';

@Component({
  selector: 'app-collage-set-detail',
  templateUrl: './collage-set-detail.component.html'
})
export class CollageSetDetailComponent implements OnInit, OnDestroy {
  @ViewChild('relatedScroll') relatedScrollRef!: ElementRef;
  @ViewChild('fileInput') fileInputRef!: ElementRef;

  isLoading = true;
  loadError = false;

  // API data
  collageLayout: any = null;
  frameDetail: any[] = [];
  frames: any[] = [];
  mounts: any[] = [];
  glasses: any[] = [];
  canvasWraps: any[] = [];
  printSurface: any = null;
  productType: any = null;
  product: any = null;
  layoutSpaceFrames: any[] = [];
  isSingleFrame = false;

  // UI state
  activeImageIndex = 0;
  selectedFrameIndex = 0;
  showSpaceView = false;

  // Space image zoom (scroll) & pan (drag)
  zoomScale = 1;
  zoomTranslateX = 0;
  zoomTranslateY = 0;
  isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartTransX = 0;
  private dragStartTransY = 0;
  inch = 8;
  startIndexX = 10;
  startIndexY = 10;

  // Frame customization
  availableFrameOptions: any[] = [];
  selectedFrameOption: any = null;
  currentGlass: any = null;

  // Upload (handled by UploadDialogService)

  // Image filters
  readonly imageFilters = [
    { name: 'Normal', class: '' },
    { name: 'Grayscale', class: 'grayscale' },
    { name: 'Sepia', class: 'sepia' },
    { name: 'Blur', class: 'blur-sm' },
    { name: 'Brightness', class: 'brightness-125' },
    { name: 'Contrast', class: 'contrast-125' },
  ];

  // Pricing
  finalPrice = 0;
  originalPrice = 0;
  discountRate = 0;
  savingsAmount = 0;

  // Related
  relatedProducts: any[] = [];

  // Canvas size for preview
  canvasWidth = 0;
  canvasHeight = 0;

  private sub = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private collageService: CollageSetService,
    private state: AppStateService,
    private auth: AuthService,
    private uploadDialog: UploadDialogService,
    private pricingDialog: PricingDetailDialogService,
    private dataLayer: DataLayerService,
  ) {}

  openPricingDetail() {
    this.pricingDialog.open({
      frameDetail: this.frameDetail,
      collageLayout: this.collageLayout,
      productType: this.productType,
      product: this.product
    });
  }

  ngOnInit() {
    // Listen for upload results from the dialog
    this.sub.add(this.uploadDialog.result$.subscribe(result => {
      if (result && this.frameDetail[result.frameIndex]) {
        this.frameDetail[result.frameIndex].backgroundImage = result.url;
        this.frameDetail[result.frameIndex].isImageUpload = 1;
      }
    }));

    this.sub.add(this.route.params.subscribe(params => {
      const slug = params['slug'] || params['caption'];
      if (slug) {
        this.loadProduct(slug);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }));
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  private loadProduct(slug: string) {
    this.isLoading = true;
    this.loadError = false;
    const userId = this.auth.currentUser?.id || 0;

    this.collageService.initArtsetCustomization(slug, userId).subscribe({
      next: (res: any) => {
        this.collageLayout = res.collageLayout || {};
        this.frameDetail = res.frameDetail || [];
        this.frames = res.frames?.data || res.frames || [];
        this.mounts = res.mounts?.data || res.mounts || [];
        this.glasses = res.glasses?.data || res.glasses || [];
        this.canvasWraps = res.canvasWraps?.data || res.canvasWraps || [];
        this.printSurface = res.printSurface || res.papers?.data?.[0] || null;
        this.productType = res.productType || {};
        this.product = res.product || {};
        this.layoutSpaceFrames = res.layoutSpaceFrames || [];
        this.currentGlass = res.glass || (this.glasses.length > 0 ? this.glasses[0] : null);
        this.isSingleFrame = this.collageLayout?.collage_type?.is_single_frame === 1;
        this.buildFrameOptions();
        this.calculateCanvasSize();
        this.calculatePricing();
        this.loadRelated();
        this.isLoading = false;

        // Cache slug → id mapping so wishlist can navigate by slug
        // even for items saved before the moveToWishlist fix.
        const clId = this.collageLayout?.id;
        const clSlug = this.collageLayout?.slug || this.collageLayout?.caption || '';
        if (clId && clSlug) {
          try {
            const stored = JSON.parse(localStorage.getItem('collage_prices') || '{}');
            stored[clId] = { ...stored[clId], slug: clSlug };
            localStorage.setItem('collage_prices', JSON.stringify(stored));
          } catch {}
        }
      },
      error: () => { this.loadError = true; this.isLoading = false; }
    });
  }

  // Calculate canvas size for frame preview
  private calculateCanvasSize() {
    let maxRight = 0, maxBottom = 0;
    this.frameDetail.forEach((f: any) => {
      const right = (f.initialX || 0) + (f.width || 0) * this.inch + (f.frameBorderWidth || 0) * 2 + (f.mount_size_1 || 0) * 2;
      const bottom = (f.initialY || 0) + (f.height || 0) * this.inch + (f.frameBorderWidth || 0) * 2 + (f.mount_size_1 || 0) * 2;
      if (right > maxRight) maxRight = right;
      if (bottom > maxBottom) maxBottom = bottom;
    });
    this.canvasWidth = maxRight + this.startIndexX * 2;
    this.canvasHeight = maxBottom + this.startIndexY * 2;
  }

  // Frame options — show all available frames from API
  private buildFrameOptions() {
    this.availableFrameOptions = (this.frames || []).filter((f: any) => f.L_frame_image || f.whole_frame_image);
    // Set current selected based on what's used in the collage
    const currentFrameId = this.frameDetail[0]?.frame_id;
    this.selectedFrameOption = this.availableFrameOptions.find((f: any) => f.id === currentFrameId)
      || this.availableFrameOptions[0] || null;
  }

  selectFrameOption(frame: any) {
    this.selectedFrameOption = frame;
    // Update ALL frames in the preview with the new frame border
    const borderImage = frame.whole_frame_image
      ? `url('${frame.whole_frame_image}') 50 stretch stretch`
      : (frame.L_frame_image ? `url('${frame.L_frame_image}') 50 stretch stretch` : 'none');
    const borderWidth = frame.border || this.frameDetail[0]?.frameBorderWidth || 12;

    this.frameDetail.forEach((fd: any) => {
      fd.frame_id = frame.id;
      fd.frame_name = frame.name;
      fd.frameBorderImage = borderImage;
      fd.frameBorderWidth = borderWidth;
    });

    // Recalculate canvas size since border width may change
    this.calculateCanvasSize();
  }

  // Frame preview helpers
  isFrameEmpty(frame: any): boolean {
    return !frame.backgroundImage || frame.backgroundImage === 'undefined' || frame.backgroundImage === DEFAULT_IMAGE;
  }

  getFramePreviewStyle(frame: any): any {
    return {
      'position': 'absolute',
      'border-width': (frame.frameBorderWidth || 0) + 'px',
      'border-style': 'solid',
      'border-image': frame.frameBorderImage || 'none',
      'top': (this.startIndexY + (frame.initialY || 0)) + 'px',
      'left': (this.startIndexX + (frame.initialX || 0)) + 'px',
      'opacity': frame.is_active === false ? 0.3 : 1,
      'cursor': 'pointer'
    };
  }

  getImageStyle(frame: any): any {
    return {
      'width': (frame.width || 0) * this.inch + 'px',
      'height': (frame.height || 0) * this.inch + 'px',
      'transform': frame.flip ? 'rotate(' + frame.flip + 'deg)' : 'none'
    };
  }

  // Upload photo — opens the root-level dialog
  onFrameClick(index: number) {
    this.selectedFrameIndex = index;
    this.uploadDialog.open(index, 'Frame ' + (index + 1));
  }


  // Image filter
  applyFilter(frameIndex: number, filterClass: string) {
    this.frameDetail[frameIndex].image_class = filterClass;
  }

  // Pricing — use collageLayout.final_price (same price shown in listing)
  // originalPrice = final_price (MRP), finalPrice = final_price * 0.85 (15% off)
  private calculatePricing() {
    if (this.collageLayout?.final_price) {
      this.originalPrice = Number(this.collageLayout.final_price);
      this.discountRate = 15;
      this.savingsAmount = Number((this.originalPrice * 0.15).toFixed(2));
      this.finalPrice = Number((this.originalPrice - this.savingsAmount).toFixed(2));
    }
  }

  // Space/room images — skip 'hover' type (duplicate of thumbnail)
  get spaceImages(): string[] {
    const imgs: string[] = [];
    const sorted = [...this.layoutSpaceFrames].sort((a: any, b: any) => (a.frontend_order || 0) - (b.frontend_order || 0));
    sorted.forEach((sf: any) => {
      const url = sf.image_path || sf.image;
      if (!url) return;
      // Skip hover and thumbnail types — duplicates of space views
      if (sf.type === 'hover' || sf.type === 'thumbnail') return;
      if (!imgs.includes(url)) imgs.push(url);
    });
    if (imgs.length === 0) {
      if (this.collageLayout?.list_thumbnail) imgs.push(this.collageLayout.list_thumbnail);
    }
    return imgs;
  }

  get activeProductImage(): string {
    return this.spaceImages[this.activeImageIndex] || this.collageLayout?.list_thumbnail || '';
  }

  // Specification helpers
  getPrintSize(frame: any): string { return `${frame.width} x ${frame.height} inch`; }
  getFrameSize(frame: any): string { return frame.frameBorderWidth ? `${(frame.frameBorderWidth / this.inch).toFixed(1)} inch` : 'N/A'; }
  getMatSize(frame: any): string {
    const t = (frame.mount_size_1 || 0) + (frame.mount_size_2 || 0) + (frame.mount_size_3 || 0);
    return t > 0 ? `${(t / this.inch).toFixed(1)} inch` : 'None';
  }

  // Related
  private loadRelated() {
    this.collageService.searchCollageSets({ page: 1, images: 8, sortby: 'created_at', sortdir: 'desc' }).subscribe({
      next: (res: any) => {
        this.relatedProducts = (res?.results || []).filter((p: any) => p.slug !== this.collageLayout?.slug).slice(0, 8);
      },
      error: () => {}
    });
  }

  getRelatedPrice(item: any): number { return Math.round((item.final_price || 0) * 0.85); }

  // Scroll to zoom
  onZoomWheel(event: WheelEvent) {
    event.preventDefault();
    const delta = event.deltaY > 0 ? 0.3 : -0.3;
    const newScale = Math.max(1, Math.min(5, this.zoomScale + delta));
    if (newScale === 1) {
      this.zoomTranslateX = 0;
      this.zoomTranslateY = 0;
    }
    this.zoomScale = newScale;
  }

  // Drag to pan (mouse)
  onZoomMouseDown(event: MouseEvent) {
    if (this.zoomScale <= 1) return;
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartTransX = this.zoomTranslateX;
    this.dragStartTransY = this.zoomTranslateY;
  }

  onZoomMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    this.zoomTranslateX = this.dragStartTransX + (event.clientX - this.dragStartX) / this.zoomScale;
    this.zoomTranslateY = this.dragStartTransY + (event.clientY - this.dragStartY) / this.zoomScale;
  }

  onZoomMouseUp() {
    this.isDragging = false;
  }

  // Drag to pan (touch)
  onZoomTouchStart(event: TouchEvent) {
    if (this.zoomScale <= 1 || !event.touches[0]) return;
    this.isDragging = true;
    this.dragStartX = event.touches[0].clientX;
    this.dragStartY = event.touches[0].clientY;
    this.dragStartTransX = this.zoomTranslateX;
    this.dragStartTransY = this.zoomTranslateY;
  }

  onZoomTouchMove(event: TouchEvent) {
    if (!this.isDragging || !event.touches[0]) return;
    event.preventDefault();
    this.zoomTranslateX = this.dragStartTransX + (event.touches[0].clientX - this.dragStartX) / this.zoomScale;
    this.zoomTranslateY = this.dragStartTransY + (event.touches[0].clientY - this.dragStartY) / this.zoomScale;
  }

  // Reset zoom when switching images
  resetZoom() {
    this.zoomScale = 1;
    this.zoomTranslateX = 0;
    this.zoomTranslateY = 0;
    this.isDragging = false;
  }

  navigateToProduct(item: any) { this.router.navigate(['/collage-set', item.slug || item.id]); }
  navigateToListing() { this.router.navigate(['/print-frame/collage-set']); }

  toggleWishlist(e?: Event){
    if (e) e.stopPropagation();
    const id = this.collageLayout?.id;
    if (!id) return;
    this.state.toggleWishlist(String(id), {
      id: String(id),
      name: this.collageLayout?.caption || this.collageLayout?.title || 'Collage Set',
      image: this.collageLayout?.list_thumbnail || this.activeProductImage || '',
      price: this.finalPrice || Number(this.collageLayout?.final_price) || 0,
      wallpaper_type: 'CollageSet',
      slug: this.collageLayout?.slug || String(id),
    });
  }

  isWishlisted(): boolean {
    const id = this.collageLayout?.id;
    return !!id && this.state.wishlistIds.has(String(id));
  }

  toggleWishlistRelated(item:any, e: Event) {
    e.stopPropagation();
    if (!item.id) return;
    this.state.toggleWishlist(String(item.id), {
      id: String(item.id),
      name: item.caption || item.title || item.name || 'Collage set',
      image: item.list_thumbnail || item.hover_thumbnail || '',
      price: this.getRelatedPrice(item),
      wallpaper_type: 'CollageSet',
      slug: item.slug || String(item.id),
    });
  }

  isRelatedWishlisted(item:any): boolean {
    return !!item?.id && this.state.wishlistIds.has(String(item.id));
  }

  scrollRelated(dir: number) {
    if (this.relatedScrollRef?.nativeElement) this.relatedScrollRef.nativeElement.scrollBy({ left: dir * 260, behavior: 'smooth' });
  }

  // Add to cart
  isAddingToCart = false;

  addToCart(buyNow = false) {
    if (this.isAddingToCart) return;

    // Validation 1: Price must be calculated
    if (!this.finalPrice || this.finalPrice <= 0) {
      this.state.showToast('Price not calculated. Please configure frames first.', 'info');
      return;
    }

    // Validation 2: All active frames must have images uploaded
    const framesWithoutImage = this.frameDetail.filter(
      (f: any) => f.isImageUpload === 0 && f.is_active !== false && this.isFrameEmpty(f)
    );
    if (framesWithoutImage.length > 0) {
      this.state.showToast('Please upload Images!', 'info');
      return;
    }

    this.isAddingToCart = true;

    const INCH = 8; // 1 inch = 8 pixels in frame preview

    // Build customizedProductItems from active frames
    const items = this.frameDetail
      .filter((f: any) => f.is_active !== false)
      .map((frame: any) => {
        const w = frame.width || 0;
        const h = frame.height || 0;
        const mountSize1 = (frame.mount_size_1 || 0) / INCH;
        const mountSize2 = (frame.mount_size_2 || 0) / INCH;
        const mountSize3 = (frame.mount_size_3 || 0) / INCH;
        const frameBorder = (frame.frameBorderWidth || 0) / INCH;
        const totalExtra = (mountSize1 + mountSize2 + mountSize3 + frameBorder) * 2;
        const finishedW = w + totalExtra;
        const finishedH = h + totalExtra;
        const imageSize = `${w}X${h}`;
        const finalSize = `${finishedW.toFixed(1)}X${finishedH.toFixed(1)}`;
        const bgImage = frame.backgroundImage || '';
        const filename = bgImage.split('/').pop() || '';

        return {
          frame_id: frame.frame_id || 0,
          frame_rate: 0,
          mount_id_1: frame.mount_id_1 || 0,
          mount_rate_1: 0,
          mount_size_1: String(mountSize1),
          mount_id_2: frame.mount_id_2 || 0,
          mount_rate_2: 0,
          mount_size_2: String(mountSize2),
          mount_id_3: frame.mount_id_3 || 0,
          mount_rate_3: 0,
          mount_size_3: String(mountSize3),
          product_category_id: 0,
          paper_id: frame.paper_id || 0,
          print_rate: 0,
          canvas_wrap_id: frame.wrap_id || 0,
          canvas_rate: 0,
          glass_id: frame.glass_id || 0,
          glass_rate: 0,
          is_table_frame: 0,
          image_size: imageSize,
          final_size: finalSize,
          price: 0,
          image_detail: JSON.stringify({
            image_id: frame.image_id || '',
            image_path: bgImage,
            image_caption: '',
            filename: filename,
            image_photographer: filename.substring(0, 4) || 'user',
            size: imageSize,
            xposition: 0,
            yposition: 0,
            quality_grade: frame.quality_grade || 'Platinum'
          })
        };
      });

    const customizedProduct = {
      id: 0,
      customer_id: this.auth.currentUser?.id || 0,
      product_id: this.product?.id || 2,
      product_type_id: this.productType?.id || 5,
      quantity: 1,
      no_of_product_types: 1,
      url: `collage-set/${this.collageLayout?.slug || this.collageLayout?.caption}?collage_layout_id=${this.collageLayout?.id || 0}`,
      layout_id: 0,
      predefine_layout_id: 0,
      collage_layout_id: this.collageLayout?.id || 0,
      wallpaper_id: 0,
      on_sale_id: 0,
      gift_wrap: 0,
      gift_message: '',
      installation_pincode: null,
      frame_detail: JSON.stringify(this.frameDetail),
      // Send listing price so checkout matches PDP exactly
      pricing_detail: JSON.stringify({
        total: this.originalPrice,
        total_with_tax: this.originalPrice,
        discount: this.savingsAmount,
        discount_with_tax: this.savingsAmount,
        discount_rate: this.discountRate,
        tax: 0,
        total_tax: 0,
        amount_payable: this.finalPrice,
        gift_wrap: 0,
        gift_tax: 0,
        installation_amount: 0,
        installation_tax: 0,
        finished_size: this.collageLayout?.final_size || ''
      }),
      customizedProductItems: items
    };

    // Save collage listing price (+ slug, so checkout can navigate back to the
    // correct /collage-set/:slug URL even when the cart join omits the slug).
    const collageId = this.collageLayout?.id || 0;
    if (collageId > 0) {
      try {
        const stored = JSON.parse(localStorage.getItem('collage_prices') || '{}');
        stored[collageId] = {
          final_price: this.originalPrice,
          selling_price: this.finalPrice,
          discount_rate: this.discountRate,
          slug: this.collageLayout?.slug || this.collageLayout?.caption || ''
        };
        localStorage.setItem('collage_prices', JSON.stringify(stored));
      } catch {}
    }

    this.state.apiAddToCart(customizedProduct).subscribe({
      next: (res: any) => {
        this.isAddingToCart = false;
        // Force refresh cart count from API
        this.state.loadCartFromApi(true);

        if (buyNow) {
          this.state.showToast('Product added to cart successfully!', 'cart');
          setTimeout(() => this.router.navigate(['/checkout']), 1000);
        } else {
          this.state.showToast('Product added to cart successfully!', 'cart');
        }
      },
      error: (err: any) => {
        this.isAddingToCart = false;
        console.error('Add to cart error:', err);
        const msg = err?.error?.message || 'Could not add to cart. Please try again.';
        this.state.showToast(msg, 'info');
      }
    });
  }
}
